<template>
  <Modal :y="50" :yTransform="60" class="w-full">
    <div class="flex justify-end">
      <div @click="$emit('close-modal')" class="clickable circular p-2">
        <CloseIcon />
      </div>
    </div>
    <div class="flex justify-center">
      <h1 class="text-2xl">Add Custom TLFB Variable</h1>
    </div>
  
    <div class="pl-8 pr-8"> 
      <Table
        class="styled-table"
        :headers="[
          {
            header: 'Substance / Behavior',
            info: 'Enter up to 16 lowercase, alphabetical characters. Your entry will be displayed in each day of the timeline follow-back calendar.'
          },
          { 
            header: 'Mode / Unit',
            info: props.substanceMode !== 'All Types'
              ? 'Using up to 18 characters, provide a descriptive category for the substance/behavior. This may be a mode of substance administration (e.g. see default modes) or unit of measurment (e.g. hours, dollars, grams). This entry is optional - keep blank to not specify a mode.'
              : '',
              center: true
          },
          { 
            header: 'Category',
            center: true
          },
          'Data Type',
          {
            header: 'REDCap Field',
            info: `Enter up to 10 alphabetical characters. This variable name will populate in your REDCap project within the 'tlfb' form. Depending on your category selection, 'sub', 'med', or 'beh' will be automatically appended to your variable name.`
          }
        ]"
      >
        <TableRow>
          <Cell> 
            <InputText
              v-if="substance === 'Other'" 
              v-model="customSubstance"
              :maxlength=16
              :onlyAlpha=true
              :onlyLower=true
              class="w-36"
              @change="cleanCustomSubstance"
            />
            <Text v-else>
              {{ substance }}
            </Text>
          </Cell>
          <Cell> 
            <InputText
              v-if="substanceMode == 'Custom'"
              v-model="customMode"
              :maxlength=18
              class="w-36"
              @change="cleanCustomMode"
            />
            <p v-else class="text-md"> 
              {{ substanceMode }} 
            </p>
          </Cell>
          <Cell>
            <Select
              class="w-29"
              v-model="category"
              :placeholder="'select category'"
            >
              <option v-for="category in ['Substance', 'Medication', 'Behavior']" :key="category">
                {{ category }}
              </option>
            </Select>
          </Cell>
          <Cell> 
            {{ dataType }} 
          </Cell>
          <Cell>
            <InputText
              v-model="redcapFieldName"
              :maxlength=10
              class="w-32"
              @change="cleanRedcapFieldName"
            />
          </Cell>
        </TableRow>
      </Table>
    </div>
    
    <div class="p-3 pt-8 flex justify-center">
      <button
        @click="$emit('add-custom-substance', customSubstance, customMode, dataType, redcapFieldNameWithSuffix)"
        :disabled="disableSubmitButton"
        :class="[
          'user-action p-2 pl-2 pr-2 text-lg',
          disableSubmitButton ? 'disabled' : 'clickable'
        ]"
      >
        Submit
      </button>
    </div>
    <div v-if="error" class="pt-2">
      <Text :class="{ hidden: !error }">{{ errorMessage }}</Text>
    </div>
  </Modal>
</template>

<script setup>
import { onMounted, ref, computed, watch } from "vue"
import InputText from "./InputText.vue"
import TableRow from "./TableRow.vue"
import CloseIcon from "./CloseIcon.vue"
import Modal from "./Modal.vue"
import Table from "./Table.vue"
import Cell from "./Cell.vue"
import Select from "./Select.vue"
import Text from "../components/Text.vue"
import removeDuplicates from "../utils/removeDuplicates.js"

const props = defineProps({
  substanceOptions: {
    type: Array,
    required: true
  },
  substance: {
    type: String,
    required: true
  },
  substanceMode: {
    type: String,
    required: true
  },
  dataType: {
    type: String,
    required: true
  },
})

defineEmits(['close-modal', 'add-custom-substance'])

const errorMessage = ref("")
const error = computed(() => errorMessage.value !== "")
const syncingSubstanceDatabase = ref(false)
const customSubstance = ref("")
const customMode = ref("")
const usedRedcapFieldNames = ref([])
const redcapFieldName = ref("")
const category = ref("")

// category of custom entry modifies the ending of redcapFieldName
const redcapFieldNameWithSuffix = computed(() => {
  if (category.value) {
    const categorySuffix = {
      'Substance': '_sub',
      'Medication': '_med',
      'Behavior': '_beh'
    }[category.value]
    return redcapFieldName.value + categorySuffix
  }
  return ""
})


/* VALIDATION OF ENTRIES */ 

const validSubstance = computed(() => {
  //verifying validSubstance format is only needed when it is new, ie., not other
  return props.substance === 'Other'
    ? verifyFormat(customSubstance.value, true, false, 3)
    : true
})

const validSubstanceMode = computed(() => { return verifyFormat(customMode.value, false, true, 2) })

const validRedcapFieldName = computed(() => { 
  const formatApproved = verifyFormat(redcapFieldName.value, true, false, 3)
  const novelFieldName = !usedRedcapFieldNames.value.includes(redcapFieldNameWithSuffix.value)
  return formatApproved && novelFieldName
})

const substanceNameTaken = computed(() => {
  return props.substanceOptions.some(substance => 
    substance.name === customSubstance.value && 
    substance.substanceModes.some(mode => mode.name === customMode.value))
})

const disableSubmitButton = computed(() => {
  return !validSubstance.value || !validSubstanceMode.value || !validRedcapFieldName.value || substanceNameTaken.value || !category.value
})

function verifyFormat(entryValue, alphaOnly, blankAccepted, minLength) {
  if (blankAccepted && entryValue === '') {
    return true
  }
  const validCharacters = alphaOnly ? /^[a-zA-Z _]*$/.test(entryValue) : true
  const longEnough = entryValue.length >= minLength
  const modeIsAllTypes = ((entryValue === "All Types") && (customMode.value == "All Types"))
  return (validCharacters && longEnough) || modeIsAllTypes
}

onMounted(() => {
  customSubstance.value = props.substance === 'Other'
    ? ""
    : props.substance

  customMode.value = props.substanceMode === "Custom"
    ? ""
    : props.substanceMode

  let modes = props.substanceOptions.map(s => s.substanceModes) 
  usedRedcapFieldNames.value = removeDuplicates(modes.flatMap(m => m.map(obj => obj.redcapFieldName)))
})

function cleanUpInput(value) {
  return value
    .toLowerCase()
}

function cleanCustomSubstance() {
  if (props.substance === 'Other') {
    customSubstance.value = cleanUpInput(customSubstance.value)
  }
}

function cleanCustomMode() {
  customMode.value = cleanUpInput(customMode.value)
}

function cleanRedcapFieldName() {
  redcapFieldName.value = cleanUpInput(redcapFieldName.value)
  redcapFieldName.value = redcapFieldName.value.replace(/ /g, '')
}

</script>

<style scoped>
.add-user,
.remove-user {
  background-color: #740000;
  display: inline-block;
  font-family: inherit;
  text-align: center;
  border-radius: 4px;
  font-weight: bold;
  padding: 0 10px;
  font-size: 18px;
  height: 2.5rem;
  color: white;
  margin: 10px;
  width: auto;
}

.add-user:hover,
.remove-user:hover {
  background-color: #b53e3e;
  text-decoration: underline;
}

.user-edit-options {
  font-size: 18px;
  font-weight: bold;
}

.user-action {
  border: 1px solid rgb(156, 163, 175);
  margin-right: 10px;
  background: white;
  border-radius: 5px;
  outline: none;
}

.action:hover {
  color: #530000;
}

th {
  background-color: #740000;
  color: #ffffff;
  text-align: left;
}

.bold-text {
  font-weight: bold;
}

</style>
